import { supabase } from './supabase'

export const POSITION_ASSET_LIST_SURFACE = 'position-workspace'
export const POSITION_ASSET_LIST_COLUMNS = ['symbol', 'last', 'changePercent', 'extendedHours', 'quantity', 'pnl']
export const POSITION_ASSET_LIST_QUOTE_COLUMNS = ['symbol', 'last', 'changePercent', 'extendedHours']

export function positionAssetListColumnsForView(viewRef) {
  const supportsPositionState = viewRef?.kind === 'all-assets'
    || (viewRef?.kind === 'lifecycle' && viewRef.lifecycle === 'active')
  return [...(supportsPositionState ? POSITION_ASSET_LIST_COLUMNS : POSITION_ASSET_LIST_QUOTE_COLUMNS)]
}

export function assetListViewKey(viewRef) {
  if (viewRef?.kind === 'manual-list') return `manual-list:${viewRef.listId}`
  if (viewRef?.kind === 'lifecycle') return `lifecycle:${viewRef.lifecycle}`
  return 'all-assets'
}

export function defaultPositionAssetListConfiguration(viewRef) {
  const configuration = {
    version: 1,
    columns: positionAssetListColumnsForView(viewRef),
    sort: {
      field: viewRef?.kind === 'manual-list' ? 'manual' : 'symbol',
      direction: 'asc',
    },
    density: 'compact',
    groupBySector: viewRef?.kind === 'manual-list',
  }
  if (viewRef?.kind === 'all-assets') {
    configuration.lifecycleSections = {
      active: true,
      working: true,
      ideas: true,
      recentlyClosed: true,
    }
  }
  return configuration
}

export function normalizePositionAssetListConfiguration(viewRef, configuration) {
  const defaults = defaultPositionAssetListConfiguration(viewRef)
  const source = configuration && typeof configuration === 'object' ? configuration : defaults
  const allowedColumns = positionAssetListColumnsForView(viewRef)
  const selectedColumns = new Set(Array.isArray(source.columns) ? source.columns : defaults.columns)
  selectedColumns.add('symbol')
  const columns = allowedColumns.filter(column => selectedColumns.has(column))
  const requestedSort = source.sort && typeof source.sort === 'object' ? source.sort : defaults.sort
  const positionOnlySort = requestedSort.field === 'quantity' || requestedSort.field === 'pnl'
  const manualSortOnNonManualView = requestedSort.field === 'manual' && viewRef?.kind !== 'manual-list'
  const sort = (positionOnlySort && !allowedColumns.includes('quantity')) || manualSortOnNonManualView
    ? defaults.sort
    : {
        field: requestedSort.field || defaults.sort.field,
        direction: requestedSort.direction === 'desc' ? 'desc' : 'asc',
      }
  const normalized = {
    ...source,
    version: 1,
    columns,
    sort,
    density: source.density === 'comfortable' ? 'comfortable' : 'compact',
    groupBySector: typeof source.groupBySector === 'boolean' ? source.groupBySector : defaults.groupBySector,
  }
  if (viewRef?.kind !== 'all-assets') delete normalized.lifecycleSections
  return normalized
}

function requireRpc() {
  if (typeof supabase.rpc !== 'function') throw new Error('Asset List persistence is unavailable')
}

function rpcData(result, fallbackMessage) {
  if (result?.error) throw new Error(result.error.message || fallbackMessage)
  return result?.data
}

export async function readPositionAssetListState() {
  requireRpc()
  const result = await supabase.rpc('asset_list_read_state', {
    p_surface_id: POSITION_ASSET_LIST_SURFACE,
  })
  const state = rpcData(result, 'Could not load Asset List settings')
  return {
    lists: Array.isArray(state?.lists) ? state.lists : [],
    configurations: Array.isArray(state?.configurations) ? state.configurations : [],
  }
}

export async function readPositionAssetListView(viewRef) {
  requireRpc()
  const result = await supabase.rpc('asset_list_read_view', { p_view_ref: viewRef })
  const view = rpcData(result, 'Could not load Asset List view')
  return Array.isArray(view?.rows) ? view.rows : []
}

export async function createPositionAssetList(name) {
  requireRpc()
  const result = await supabase.rpc('asset_list_create_manual_list', { p_name: name })
  return String(rpcData(result, 'Could not create watchlist'))
}

export async function renamePositionAssetList(listId, name, expectedVersion) {
  requireRpc()
  const result = await supabase.rpc('asset_list_rename_manual_list', {
    p_list_id: listId,
    p_name: name,
    p_expected_version: expectedVersion,
  })
  return Number(rpcData(result, 'Could not rename watchlist'))
}

export async function archivePositionAssetList(listId, expectedVersion) {
  requireRpc()
  const result = await supabase.rpc('asset_list_archive_manual_list', {
    p_list_id: listId,
    p_expected_version: expectedVersion,
  })
  return Number(rpcData(result, 'Could not delete watchlist'))
}

async function authenticatedSession() {
  const { data: { session } = {} } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Authentication required')
  return session
}

export async function searchPositionAssetListInstruments(query, { signal, profile = false } = {}) {
  const session = await authenticatedSession()
  const normalized = String(query || '').trim().toUpperCase().replace(/\s+/g, ' ')
  const response = await fetch(`/api/asset-lists/instrument-search?q=${encodeURIComponent(normalized)}${profile ? '&profile=1' : ''}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${session.access_token}` },
    signal,
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    const error = new Error(body?.error || 'Could not search IBKR instruments')
    error.status = response.status
    error.code = body?.code || null
    throw error
  }
  return body
}

export async function addVerifiedEquityToPositionAssetList(listId, candidate, expectedVersion) {
  const session = await authenticatedSession()
  const response = await fetch('/api/asset-lists/verified-equity-add', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ listId, candidate, expectedVersion }),
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    const error = new Error(body?.error || 'Could not add asset to watchlist')
    error.status = response.status
    error.code = body?.code || null
    throw error
  }
  return body
}

export async function loadPositionAssetListOptionChain(
  candidate, expiry = null, { signal, center = null } = {},
) {
  const session = await authenticatedSession()
  const params = new URLSearchParams({
    symbol: String(candidate?.symbol || '').trim().toUpperCase(),
    conid: String(candidate?.conid || ''),
  })
  if (expiry) params.set('expiry', String(expiry))
  if (expiry && Number.isFinite(Number(center)) && Number(center) > 0) {
    params.set('center', String(Number(center)))
  }
  const response = await fetch(`/api/asset-lists/option-chain?${params}`, {
    method: 'GET', headers: { Authorization: `Bearer ${session.access_token}` }, signal,
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    const error = new Error(body?.error || 'Could not load IBKR option chain')
    error.status = response.status
    throw error
  }
  return body
}

export async function addVerifiedOptionToPositionAssetList(
  listId, contract, underlying, expectedVersion,
) {
  const session = await authenticatedSession()
  const response = await fetch('/api/asset-lists/verified-option-add', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ listId, contract, underlying, expectedVersion }),
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    const error = new Error(body?.error || 'Could not add option to watchlist')
    error.status = response.status
    error.code = body?.code || null
    throw error
  }
  return body
}

export async function savePositionAssetListConfiguration(viewRef, expectedVersion, configuration) {
  requireRpc()
  const result = await supabase.rpc('asset_list_upsert_view_configuration', {
    p_surface_id: POSITION_ASSET_LIST_SURFACE,
    p_view_ref: viewRef,
    p_expected_config_version: expectedVersion,
    p_configuration: configuration,
  })
  return Number(rpcData(result, 'Could not save Asset List settings'))
}

export async function removePositionAssetListMembership(listId, membershipId, expectedVersion) {
  requireRpc()
  const result = await supabase.rpc('asset_list_remove_manual_membership', {
    p_list_id: listId,
    p_membership_id: membershipId,
    p_expected_version: expectedVersion,
  })
  return Number(rpcData(result, 'Could not remove asset from list'))
}
